/**
 * @fileoverview How a date / datetime / time column filter reads and writes its terms
 *
 * Everything the user sees is driven by the column's *resolved* patterns — the user's format
 * with the field's precision applied — so an editor offers exactly the sections the cell
 * renders, and the filter matches what looks identical on screen.
 */

import { applyDatePrecision, applyTimePrecision, formatWithLocale } from "@scayla/core";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";

import type { DatetimePickerMode } from "../../DatetimePicker/datetimePickerTypes";
import type { DateFilterKind } from "../types";
import {
  dateToFilterTerm,
  filterTermToDate,
  normalizeFilterTerm,
  resolveTermShape,
  shapeHasTime,
} from "./dateFilterTerms";

export interface DateFilterFormatOptions {
  kind: MaybeRefOrGetter<DateFilterKind>;
  dateFormat: MaybeRefOrGetter<string>;
  timeFormat: MaybeRefOrGetter<string>;
  datePrecision: MaybeRefOrGetter<string | undefined>;
  timePrecision: MaybeRefOrGetter<string | undefined>;
}

export type DateFilterPickerMode = "date" | "datetime" | "time";

const RANGE_PICKER_MODES = {
  date: "dateRange",
  datetime: "datetimeRange",
  time: "timeRange",
} as const satisfies Record<DateFilterPickerMode, DatetimePickerMode>;

export function useDateFilterFormat(options: DateFilterFormatOptions) {
  const kind = computed(() => toValue(options.kind));

  const resolvedDateFormat = computed(() =>
    applyDatePrecision(toValue(options.dateFormat), toValue(options.datePrecision)),
  );
  const resolvedTimeFormat = computed(() =>
    applyTimePrecision(toValue(options.timeFormat), toValue(options.timePrecision)),
  );
  const shape = computed(() => resolveTermShape(kind.value, resolvedDateFormat.value, resolvedTimeFormat.value));

  // The editor offers exactly the sections the cells render, whatever combination of date and
  // time precision produced them.
  const pickerMode = computed<DateFilterPickerMode>(() => {
    if (kind.value === "time") return "time";
    if (kind.value === "datetime" && shapeHasTime(shape.value)) return "datetime";
    return "date";
  });

  /** The same sections as {@link pickerMode}, picked as one period (Between). */
  const rangePickerMode = computed(() => RANGE_PICKER_MODES[pickerMode.value]);

  function toTerm(date: Date | null): string {
    return date ? dateToFilterTerm(date, kind.value, shape.value) : "";
  }

  function toDate(term: string): Date | null {
    return filterTermToDate(term, kind.value);
  }

  /** A saved term re-encoded at the column's own shape; "" when it is not a date. */
  function normalizeTerm(term: string): string {
    return normalizeFilterTerm(term, kind.value, shape.value);
  }

  /** `shortYear` drops to a 2-digit year, for the Between summary that shows two dates in a narrow cell. */
  function formatTerm(term: string, { shortYear = false }: { shortYear?: boolean } = {}): string {
    const date = toDate(term);
    if (!date) return term;
    let pattern: string;
    if (kind.value === "time") pattern = resolvedTimeFormat.value;
    else if (shapeHasTime(shape.value)) pattern = `${resolvedDateFormat.value} ${resolvedTimeFormat.value}`;
    else pattern = resolvedDateFormat.value;
    return formatWithLocale(date, shortYear ? pattern.replace(/yyyy/g, "yy") : pattern);
  }

  return { shape, pickerMode, rangePickerMode, toTerm, toDate, normalizeTerm, formatTerm };
}
